<template>
  <div class="round-duration-chooser column gap-4">
    <round-unit-dropdown-extended
      :null-choosable="nullChoosable"
      :round-name="roundName || undefined"
      :round-unit="roundUnit"
      :round-length="roundLength"
      :round-customized="roundCustomized"
      @update:round-unit="$emit('update:roundUnit', $event)"
      @update:round-length="$emit('update:roundLength', $event)"
      @update:round-customized="updateRoundCustomized($event)"
      @update:round-name="$emit('update:roundName', $event)"
    />

    <section>
      <div class="label">{{ $t("labels.simulationLength") }}</div>

      <radio-button
        v-slot="{ selected }"
        :value="durationType"
        :data="('roundAmount' as const)"
        @update:value="updateDurationType($event)"
      >
        <input-number
          ref="inputRoundAmount"
          class="round-duration-chooser__roundamount"
          :value="roundAmount"
          :min="1"
          :disabled="!selected"
          :input-class="[{ 'pointer-events-none': !selected }]"
          @update:value="updateRoundAmount($event)"
        />
        <span class="whitespace-nowrap">
          <template v-if="roundLength != 1">× {{ roundLength }}</template>
          {{ $t(`roundUnits.${roundUnit}`, roundAmount * roundLength) }}
        </span>
      </radio-button>
      <radio-button
        v-slot="{ selected }"
        :value="durationType"
        :data="('timeframe' as const)"
        class="radiobutton_top"
        @update:value="updateDurationType($event)"
      >
        <div class="w-full">
          <span class="radiobutton__label pv-1">{{
            $t("actions.setTimeframe")
          }}</span>

          <div v-if="selected" class="round-duration-chooser__time">
            <span class="round-duration-chooser__timelabel">{{
              $t("labels.start")
            }}</span>
            <round-time-select
              :value="timeStart || '2020-01-01T00:00:00.000Z'"
              :round-unit="roundUnit"
              :round-length="roundLength"
              @update:value="updateTimeStart($event)"
            />

            <span class="round-duration-chooser__timelabel">{{
              $t("labels.end")
            }}</span>
            <round-time-select
              :value="timeEnd || '2020-01-01T00:00:00.000Z'"
              :round-unit="roundUnit"
              :round-length="roundLength"
              :min="minEndTime"
              time-type="end"
              @update:value="updateTimeEnd($event)"
            />
          </div>
        </div>
      </radio-button>
    </section>

    <!--<code>
      {{ timeStart }}<br>
      {{ minEndTime }}
    </code>
    <code>
      {{ timeEnd }}
    </code>-->

    <popup-dialog
      :visible="data.chooseTimePopupOpen"
      :width="500"
      :close-button-visible="true"
      :title="$t('actions.setRoundUnit')"
      :resizable="false"
      style="overflow: visible"
      @close="rejectTimePopup?.()"
    >
      <div class="column gap-4 p-4 pt-6">
        <p class="m-0">
          {{ $t("messages.timeframeForTimeUnitsOnly") }}
        </p>
        <round-unit-dropdown-extended
          :round-unit="roundUnit"
          :round-length="roundLength"
          :round-customized="roundCustomized"
          @update:round-unit="$emit('update:roundUnit', $event)"
          @update:round-length="$emit('update:roundLength', $event)"
          @update:round-customized="$emit('update:roundCustomized', $event)"
        />

        <div class="line gap-2 justify-between">
          <button type="button" class="button" @click="rejectTimePopup?.()">
            {{ $t("labels.abort") }}
          </button>
          <button
            type="button"
            class="button"
            :disabled="roundUnit == 'round'"
            @click="resolveTimePopup?.()"
          >
            {{ $t("labels.ok") }}
          </button>
        </div>
      </div>
    </popup-dialog>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, watch, nextTick, useTemplateRef } from "vue";

import InputNumber from "@core/input/InputNumber.vue";
import RadioButton from "@core/input/RadioButton.vue";
import RoundTimeSelect from "./RoundTimeSelect.vue";
import PopupDialog from "@core/ui/PopupDialog.vue";
import RoundUnitDropdownExtended from "./RoundUnitDropdownExtended.vue";

import {
  getEndTimeInDuration,
  getMinEndTime,
  getRoundAmountFromTime,
  moveDateToUnit
} from "@editor/items/simulationRound/useRoundTime";

import type { RoundUnit } from "./SimulationRoundItem";

const props = withDefaults(defineProps<{
  roundUnit: RoundUnit;
  roundLength: number;
  roundAmount: number;
  durationType: "roundAmount" | "timeframe";
  timeStart?: string | null;
  timeEnd?: string | null;
  roundCustomized?: boolean;
  roundName?: string | undefined | null;
  nullChoosable?: boolean;
}>(), {
  timeStart: null,
  timeEnd: null,
  roundCustomized: false,
  roundName: undefined,
  nullChoosable: false
});

const emit = defineEmits<{
  (e: "update:roundUnit", value: RoundUnit | "custom" | null): void;
  (e: "update:roundLength", value: number): void;
  (e: "update:roundAmount", value: number): void;
  (e: "update:durationType", value: "roundAmount" | "timeframe"): void;
  (e: "update:timeStart", value: string): void;
  (e: "update:timeEnd", value: string | null): void;
  (e: "update:roundCustomized", value: boolean | null): void;
  (e: "update:roundName", value: string | null): void;
}>();

const inputRoundAmount = useTemplateRef("inputRoundAmount");

const data = reactive({
  chooseTimePopupOpen: false
});

let resolveTimePopup: (() => void) = () => {};
let rejectTimePopup: (() => void) = () => {};

const minEndTime = computed(() => {
  if (props.timeStart == null) return;
  return getMinEndTime(props.roundUnit, props.timeStart);
});

watch(
  () => props.roundUnit, 
  (roundUnit: RoundUnit, oldRoundUnit: RoundUnit) => {
    if (roundUnit === "round") {
      // rounds can't be used with timeframe settings
      emit("update:durationType", "roundAmount");
    } else {
      // make sure there the simulation limits are set.
      const newTimeStart = moveDateToUnit(
        roundUnit,
        oldRoundUnit,
        props.timeStart ?? new Date(),
        "start"
      );
      const newEndTimeInDuration = getEndTimeInDuration(
        newTimeStart,
        roundUnit,
        props.roundLength,
        props.roundAmount
      );

      let newTimeEnd: string | null = null;
      if (props.timeEnd) {
        newTimeEnd = moveDateToUnit(
          roundUnit,
          oldRoundUnit,
          props.timeEnd ?? newEndTimeInDuration, // set end if none
          "end"
        );
      } else {
        newTimeEnd = null;
      }

      if (props.durationType === "timeframe") {
        if (newTimeStart == null || newTimeEnd == null) {
          console.warn("time start or end is null");
          return;
        }

        adjustRoundAmountToTimeframe(
          roundUnit,
          props.roundLength,
          newTimeStart,
          newTimeEnd
        );
      } else {
        if (newEndTimeInDuration == null) {
          console.warn("newEndTimeInDuration is null");
        } else {
          newTimeEnd = moveDateToUnit(
            roundUnit,
            oldRoundUnit,
            newEndTimeInDuration,
            "end"
          );
          emit("update:timeEnd", newTimeEnd);
        }
      }

      if (newTimeEnd == null) {
        console.warn("time end is null");
        return;
      }

      const dMinEnd = new Date(getMinEndTime(roundUnit, newTimeStart));
      if (new Date(newTimeEnd) < dMinEnd) {
        newTimeEnd = dMinEnd.toISOString();
      }

      emit("update:timeStart", newTimeStart);
      emit("update:timeEnd", newTimeEnd);
    }
  }
);

async function updateDurationType(type: "roundAmount" | "timeframe") {
  // Prevent setting type to timeframe while unit is round:
  if (type === "timeframe" && props.roundUnit === "round") {
    data.chooseTimePopupOpen = true;

    try {
      await new Promise((resolve, reject) => {
        resolveTimePopup = resolve as () => void;
        rejectTimePopup = reject;
      });
    } catch (error) {
      console.log(error);
    } finally {
      data.chooseTimePopupOpen = false;
      resolveTimePopup = () => {};
      rejectTimePopup = () => {};
    }

    if (props.roundUnit === "round") {
      return;
    }
  }
  emit("update:durationType", type);

  if (type === "roundAmount") {
    nextTick(() => {
      inputRoundAmount.value?.focus();
    });
  }
}

function updateRoundAmount(amount: number | null) {
  if (amount == null) {
    return;
  }
  emit("update:roundAmount", amount);
  emit(
    "update:timeEnd",
    getEndTimeInDuration(
      props.timeStart,
      props.roundUnit,
      props.roundLength,
      amount
    )
  );
}

function updateTimeStart(value: string) {
  emit("update:timeStart", value);

  if (props.timeEnd == null) {
    console.warn("timeStart is null");
    return;
  }

  adjustRoundAmountToTimeframe(
    props.roundUnit,
    props.roundLength,
    value,
    props.timeEnd
  );
}

function updateTimeEnd(value: string) {
  emit("update:timeEnd", value);

  if (props.timeStart == null) {
    console.warn("timeStart is null");
    return;
  }

  adjustRoundAmountToTimeframe(
    props.roundUnit,
    props.roundLength,
    props.timeStart,
    value
  );
}

function adjustRoundAmountToTimeframe(
  roundUnit: RoundUnit,
  roundLength: number,
  timeStart: string,
  timeEnd: string
) {
  const roundAmount = getRoundAmountFromTime(
    roundUnit,
    roundLength,
    timeStart,
    timeEnd
  );
  if (roundAmount > 0) {
    emit("update:roundAmount", roundAmount);
  } else {
    emit("update:roundAmount", 1);
  }
}

function updateRoundCustomized(value: boolean | null) {
  if (value != true) {
    emit("update:roundLength", 1);
  }
  emit("update:roundCustomized", value);
}
</script>

<style lang="scss">
.round-duration-chooser {
  &__roundamount {
    max-width: 5rem;
  }

  &__customoptions {
    display: grid;
    grid-template-columns: 5rem minmax(0, 1fr);
  }

  &__time {
    display: grid;
    gap: 0.25rem;
    grid-template-columns: min-content minmax(0, 1fr);
  }

  &__timelabel {
    align-self: start;
    padding-block-start: 0.5rem;
    padding-block-end: 0.5rem;
  }
}
</style>
